import {
  CheckoutFlowStep,
  isCheckoutMethodFieldReadOnly,
  selectCurrencyOptions,
  useAppSelector,
  useBanksRequest,
  useCancelPaymentRequest,
  useCheckoutPayment,
  useEventTracker,
  useSessionId,
  useUnloadBeacon
} from 'mmfintech-checkout-commons'
import type { CheckoutMethodField, CountryChoice, SupportedBank } from 'mmfintech-checkout-commons/types'
import { getCountryAlpha2, isEmptyString, isValidArray, parseJson, tr, translateCountryName } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useCallback, useEffect, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  BackToMerchantButton,
  CheckoutContainer,
  CoreButton,
  CoreInput,
  CoreSelect,
  CustomPreloader,
  MerchantPane,
  SelectMethod
} from '../../components'
import { DepositResult } from '../DepositResult'
import { WalletCheckout } from '../WalletCheckout'
import { WalletCheckoutOtp } from '../WalletCheckout/WalletCheckoutOtp'

const Checkout = () => {
  const sessionId = useSessionId()

  const {
    logo,
    checkoutCountries,
    checkoutPay,
    checkoutPayError,
    checkoutPayFetching,
    session,
    sessionError,
    sessionFetching
  } = useSelector(
    ({
      checkout: {
        logo,
        checkoutCountries,
        checkoutPay,
        checkoutPayError,
        checkoutPayFetching,
        session,
        sessionError,
        sessionFetching
      }
    }) => ({
      logo,
      checkoutCountries,
      checkoutPay,
      checkoutPayError,
      checkoutPayFetching,
      session,
      sessionError,
      sessionFetching
    }),
    shallowEqual
  )

  const [sortedCurrencies, setSortedCurrencies] = useState(null)
  const [storageAccess, setStorageAccess] = useState(false)

  const history = useHistory()
  useUnloadBeacon(sessionId)

  const currencies = useAppSelector(selectCurrencyOptions)

  const { trackEvent } = useEventTracker()
  const { cancelPayment } = useCancelPaymentRequest()

  const showCancelButton = true
  const {
    step,
    // setStep,
    fieldValues,
    status,
    countryCode,
    setCountryCode,
    paymentMethod,
    setPaymentMethod,
    handleStartOver,
    handlePay,
    handlePayWithWallet,
    handlePaymentStatusResponse
  } = useCheckoutPayment({
    initialStep: CheckoutFlowStep.SELECT_METHOD,
    enableKingdomWalletStep: true
  })

  const { banks } = useBanksRequest()

  const handleCancelClick = () => {
    if (sessionId) {
      cancelPayment(sessionId)
    }
    void trackEvent(sessionId, 'cancelled_by_payer')
    history.push('/fail')
    // return false
  }

  const handleBankSelected = (code: string) => {
    void trackEvent(sessionId, 'bank_selected', code)
  }

  const prepareResponse = useCallback(() => {
    if (session?.sessionStatus?.value === 'IN_PROGRESS') {
      return {
        result: parseJson(session?.paymentResult, session?.paymentResult),
        resultType: session?.paymentResultType,
        processingAmount: session?.processingAmount,
        processingCurrency: session?.processingCurrency
      }
    }
    return checkoutPay
  }, [checkoutPay, session])

  useEffect(() => {
    if (typeof document.hasStorageAccess === 'function') {
      document.hasStorageAccess().then(response => setStorageAccess(response))
    } else {
      setStorageAccess(true)
    }
  }, [])

  useEffect(() => {
    if (isValidArray(currencies)) {
      const sorted = [...currencies].sort()
      setSortedCurrencies(sorted.map(value => ({ value, label: value })))
    } else {
      setSortedCurrencies(null)
    }
  }, [currencies])

  return (
    <CheckoutContainer>
      <div className='checkout-content-wrapper'>
        {sessionFetching ? (
          <div className='payment-wrapper'>
            <CustomPreloader />
          </div>
        ) : (
          <>
            {step !== CheckoutFlowStep.KINGDOM_WALLET && <MerchantPane session={session} logo={logo} />}

            {(step === CheckoutFlowStep.SELECT_METHOD || step === CheckoutFlowStep.ADDITIONAL_FIELDS) && (
              <div className='payment-wrapper'>
                {status === 'IDLE' && (
                  <>
                    {checkoutCountries?.length > 1 ? (
                      <div className='mb-3'>
                        <CoreSelect
                          type='country'
                          name='countryCode'
                          label={tr('CHECKOUT.PAYMENT.COUNTRY_PLACEHOLDER', 'Select country')}
                          value={countryCode}
                          options={checkoutCountries?.map((country: CountryChoice) => ({
                            value: country.countryCode,
                            label: country.name
                          }))}
                          onChange={(_name: string, value: string) => {
                            return setCountryCode(value)
                          }}
                          required
                        />
                      </div>
                    ) : (
                      <div className='mb-3'>
                        <CoreInput
                          name='countryCode'
                          label={tr('CHECKOUT.PAYMENT.COUNTRY', 'Country')}
                          value={translateCountryName(countryCode)}
                        />
                      </div>
                    )}

                    <SelectMethod method={paymentMethod} setMethod={setPaymentMethod} countryCode={countryCode} />

                    <div className='mt-2' />
                    {isValidArray(fieldValues.fields) && paymentMethod !== 'KINGDOM_WALLET' ? (
                      <div className='payment-fields-wrapper'>
                        {fieldValues.fields.map((field: CheckoutMethodField) => {
                          if (fieldValues.isFieldHidden(field)) {
                            return null
                          }

                          if (field.name === 'bankChoiceId') {
                            return (
                              <CoreSelect
                                key={field.name}
                                type='default'
                                label={tr('CHECKOUT.PAYMENT.BANK', 'Bank')}
                                {...fieldValues.registerInput(field.name, handleBankSelected)}
                                options={banks?.map((bank: SupportedBank) => {
                                  const { bankChoiceId, name } = bank
                                  return { value: bankChoiceId, label: name }
                                })}
                              />
                            )
                          }

                          if (field.name === 'currency') {
                            return (
                              <CoreSelect
                                key={field.name}
                                type='currency'
                                label={
                                  field.translationKey
                                    ? tr(field.translationKey, field.defaultLabel)
                                    : field.defaultLabel
                                }
                                {...fieldValues.registerInput(field.name)}
                                options={sortedCurrencies || []}
                                onChange={(name, value) => fieldValues.setValue(name, value.value)}
                                required={field.required}
                              />
                            )
                          }

                          if (field.name === 'documentId' && paymentMethod === 'PIX') {
                            return (
                              <CoreInput
                                key={field.name}
                                type={'text'}
                                label={
                                  field.translationKey
                                    ? tr(field.translationKey, field.defaultLabel)
                                    : field.defaultLabel
                                }
                                {...fieldValues.registerShort(field.name)}
                                onChange={(name: string, value: string) =>
                                  fieldValues.setValue(name, value.replace(/[ ./-]/g, ''))
                                }
                                required={fieldValues.isFieldRequired(field)}
                                autoComplete='off'
                              />
                            )
                          }

                          if (field.name === 'phone') {
                            return (
                              <CoreInput
                                type='phone'
                                data-test={`${field.name}-input`}
                                label={
                                  field.translationKey
                                    ? tr(field.translationKey, field.defaultLabel)
                                    : field.defaultLabel
                                }
                                {...fieldValues.registerInput(field.name)}
                                disabled={isCheckoutMethodFieldReadOnly(field)}
                                autoComplete='off'
                                {...(paymentMethod === 'INTERAC'
                                  ? {
                                      country: 'ca',
                                      onlyCountries: ['ca'],
                                      countryCodeEditable: false,
                                      disableDropdown: true
                                    }
                                  : {
                                      country: getCountryAlpha2(countryCode)?.toLowerCase()
                                    })}
                              />
                            )
                          }

                          return (
                            <CoreInput
                              key={field.name}
                              type={field.sensitive ? 'password' : 'text'}
                              label={
                                field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel
                              }
                              {...fieldValues.registerInput(field.name)}
                              readOnly={isCheckoutMethodFieldReadOnly(field)}
                              autoComplete='off'
                            />
                          )
                        })}
                      </div>
                    ) : null}
                  </>
                )}

                <ErrorDisplay error={[sessionError, checkoutPayError]} />

                {status === 'IDLE' && (
                  <div className='buttons'>
                    <CoreButton
                      type='button'
                      fullWidth
                      variation='primary'
                      text={tr('CHECKOUT.PAYMENT.BUTTON_PAY', 'Pay')}
                      onClick={handlePay}
                      disabled={isEmptyString(paymentMethod)}
                      isLoading={checkoutPayFetching}
                      data-test='button-pay'
                    />
                    {showCancelButton && (
                      <CoreButton
                        type='button'
                        variation='tertiary'
                        text={tr('CHECKOUT.BUTTONS.CANCEL', 'Cancel')}
                        onClick={handleCancelClick}
                        disabled={checkoutPayFetching}
                        data-test='button-pay'
                      />
                    )}
                  </div>
                )}

                <BackToMerchantButton className='mt-3' returnUrl={session?.returnUrl} />
              </div>
            )}
          </>
        )}

        {step === CheckoutFlowStep.KINGDOM_WALLET && (
          <WalletCheckout
            sessionId={sessionId}
            onCancel={handleStartOver}
            handlePay={handlePayWithWallet}
            hasStorageAccess={storageAccess}
            setStorageAccess={setStorageAccess}
          />
        )}

        {step === CheckoutFlowStep.WALLET_OTP && (
          <WalletCheckoutOtp onSuccess={response => handlePaymentStatusResponse(response)} wallet={true} />
        )}

        {step === CheckoutFlowStep.DEPOSIT_RESULT && (
          <DepositResult session={session} sessionId={sessionId} response={prepareResponse()} />
        )}
      </div>
    </CheckoutContainer>
  )
}

export default Checkout
